/*
 * What to call an indicator's lines, in words.
 *
 * The chart names an indicator's series the way its code does: MACD has "DIF",
 * "DEA" and "MACD", and the last of those is the HISTOGRAM, not the MACD line.
 * Shown raw ("MACD | MACD | 12,26,9") that is a trap, and someone building a
 * MACD cross picked the histogram believing it was the line (alert 714). The
 * picker, the row, and the plain-English summary all use these names instead.
 *
 * The STORED names do not change: alarm_type_variable / first_variable stay
 * 'MACD' / 'DIF' etc. because the worker keys on them. This is display only.
 *
 * Pure: no DOM. Text is translated through window.t('ind.<key>') when it is
 * there; each entry carries its English so Node (the tests) works without it.
 */

// How an indicator is shown when its catalogue shortName is not what a trader says.
const DISPLAY = { STOCHF: 'Stoch', STOCH: 'Stoch', STOCHRSI: 'StochRSI', DONCHIAN: 'Donchian', KELTNER: 'Keltner' };

/*
 * Lines that need explaining, by indicator then output (both upper-case).
 * `key` is the i18n key, `en` the label with {name} for the indicator's name,
 * `hint` an English one-liner (key + '.hint' in the catalogue).
 */
const MACD_LINES = {
  DIF: { key: 'macd_line', en: '{name} line', hint: 'Fast average minus slow average.' },
  DEA: { key: 'signal_line', en: '{name} signal line', hint: 'An average of the MACD line. The usual one to compare it with.' },
  MACD: { key: 'histogram', en: '{name} histogram', hint: 'MACD line minus signal line. On a different scale from both.' },
};
const STOCH_LINES = {
  K: { key: 'k_line', en: '{name} %K', hint: 'The fast line: where the price sits in its recent range.' },
  D: { key: 'd_line', en: '{name} %D', hint: 'The signal line: an average of %K.' },
};
const BAND_LINES = {
  UP: { key: 'upper_band', en: '{name} upper band', hint: 'The average plus the set number of deviations.' },
  MID: { key: 'middle_band', en: '{name} middle band', hint: 'The moving average itself.' },
  DN: { key: 'lower_band', en: '{name} lower band', hint: 'The average minus the set number of deviations.' },
};
const CHANNEL_LINES = {
  UPPER: { key: 'upper_channel', en: '{name} upper channel', hint: 'The top edge of the channel.' },
  MID: { key: 'middle_channel', en: '{name} middle channel', hint: 'The middle of the channel.' },
  LOWER: { key: 'lower_channel', en: '{name} lower channel', hint: 'The bottom edge of the channel.' },
};
const AVERAGE = { key: 'average_line', en: '{name} average', hint: 'A moving average of the line above.' };

const LINES = {
  MACD: MACD_LINES,
  STOCHF: STOCH_LINES,
  STOCH: STOCH_LINES,
  STOCHRSI: STOCH_LINES,
  BOLL: BAND_LINES,
  DONCHIAN: CHANNEL_LINES,
  KELTNER: CHANNEL_LINES,
  AROON: {
    AROONUP: { key: 'aroon_up', en: 'Aroon Up', hint: 'How recently the highest high was made.' },
    AROONDN: { key: 'aroon_down', en: 'Aroon Down', hint: 'How recently the lowest low was made.' },
  },
  OBV: { MAOBV: AVERAGE },
  ROC: { MAROC: AVERAGE },
  MTM: { MAMTM: AVERAGE },
  TRIX: { MATRIX: AVERAGE },
  WR: {
    WR1: { key: 'wr_1', en: '{name} line 1', hint: 'Uses the first period setting.' },
    WR2: { key: 'wr_2', en: '{name} line 2', hint: 'Uses the second period setting.' },
    WR3: { key: 'wr_3', en: '{name} line 3', hint: 'Uses the third period setting.' },
  },
};

const up = (v) => String(v === null || v === undefined ? '' : v).trim().toUpperCase();

function fill(text, vars) {
  return String(text).replace(/\{(\w+)\}/g, (m, k) => (vars && vars[k] !== undefined ? vars[k] : m));
}

// the catalogue's translation if there is one, else the English written here
function tr(key, english, vars) {
  try {
    if (typeof window !== 'undefined' && typeof window.t === 'function') {
      const out = window.t(key, vars);
      if (out && out !== key) return out;
    }
  } catch (e) { /* fall back to English */ }
  return fill(english, vars);
}

/* "Stoch" for STOCHF, otherwise the name as the chart shows it. */
export function indicatorDisplayName(name, shortName) {
  return DISPLAY[up(name)] || shortName || String(name || '');
}

/* The parameters as a list of numbers, from calcParams ([12, 26, 9]) or the stored
   JSON ('{"param1":12,...}'). */
export function paramList(raw) {
  if (raw === null || raw === undefined || raw === '') return [];
  let value = raw;
  if (typeof raw === 'string') {
    try { value = JSON.parse(raw); } catch (e) { return []; }
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) value = Object.values(value);
  return Array.isArray(value) ? value.filter((v) => v !== null && v !== undefined && v !== '') : [];
}

/*
 * One line of an indicator: {text, hint}. `text` is the line named in words, with
 * the indicator's name in it ("MACD histogram"); `hint` is one sentence, or '' when
 * the line needs none. Indicators with a single line are just their name.
 */
export function lineLabel(name, output, shortName) {
  const display = indicatorDisplayName(name, shortName);
  const entry = (LINES[up(name)] || {})[up(output)];
  if (!entry) return { text: display, hint: '' };
  return {
    text: tr('ind.' + entry.key, entry.en, { name: display }),
    hint: tr('ind.' + entry.key + '.hint', entry.hint),
  };
}

/* True when this indicator has more than one line worth telling apart. */
export function hasNamedLines(name) {
  return !!LINES[up(name)];
}

/*
 * The whole thing as one phrase: "MACD histogram (12, 26, 9)", "RSI (14)".
 * What the condition row shows once a line is chosen.
 */
export function friendlyLine(name, output, params, shortName) {
  const label = lineLabel(name, output, shortName).text;
  const list = paramList(params);
  return list.length ? label + ' (' + list.join(', ') + ')' : label;
}
